/**
 * identity-brief.ts — Editorial copy layer for the User Analytics standfirst.
 *
 * Mirrors `posture-brief.ts` for the Identity pillar: composes the daily brief
 * (kicker, headline, deck, verdict, byline) from the active identity-pillar
 * state. Voice = "trusted advisor": short sentences, no em-dashes, named
 * subjects ahead of row counts.
 *
 * Identity-specific cues (per CLAUDE.md "Product Voice" + posture-pages-redesign §8):
 *   - Subject varies — "MFA" / "People" / "Adoption" — picked deterministically per seed
 *   - Modifier states by score band: there / almost there / mid-stride / lagging
 *   - Weak-link clause leads with a named subject ("Eight people need a hand.")
 *   - Verdict line stays inside the six-word ScoreBox cap
 *
 * Variant tables are exported under `__TEST_ONLY__` so the vitest suite can
 * shape-assert without re-listing every string.
 */

import { cap, formatKicker, localDayKey, pickVariant } from './brief-helpers'

export type Pillar = 'compliance' | 'bpa' | 'identity'

export interface IdentityBrief {
  /** Pill above the headline: "Today's Brief · 29 April" */
  kicker: string
  /** First word(s) of headline rendered roman, e.g. "MFA is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "almost there" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Complete, capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer: "Brief by Guardian Continuous Assessment · For {user}" */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
}

/* -------------------------------------------------------------------------- */
/* Input shape                                                                */
/* -------------------------------------------------------------------------- */

export interface IdentityBriefInput {
  /** Currently signed-in user email, e.g. "adam.jones@iuvotech.com" */
  userEmail?: string
  /**
   * Active tenant ID. Combined with the local day to seed variant selection
   * so the brief is stable per tenant per day. Falls back to a constant when
   * absent (still deterministic, just shared across tenants).
   */
  tenantId?: string
  /** MFA member adoption percent (0-100) — same number rendered on PillarIdentity. */
  score?: number | null
  /** 30-day score delta — drives the headline modifier and the score-box pill. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact identity action — informs the verdict + deck risk slot. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Count of people needing attention — drives the weak-link clause. Typically
   *  `noMfaEnabledUsers` + critical-tier user count. */
  criticalCount?: number
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function imperativeFromAction(title?: string): string {
  if (!title) return 'triage'
  const first = title.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '') ?? ''
  const known = new Set([
    'enforce',
    'roll',
    'reclaim',
    'review',
    'remove',
    'rotate',
    'restrict',
    'require',
    'rebuild',
    'remediate',
    'audit',
    'patch',
    'disable',
    'block',
    'tighten',
    'triage',
    'address',
    'enrol',
    'enroll',
    'invite',
    'send',
  ])
  return known.has(first) ? first : 'triage'
}

/* -------------------------------------------------------------------------- */
/* Headline subject (varies — MFA / People / Adoption)                        */
/* -------------------------------------------------------------------------- */

/**
 * Identity headline subjects rotate so the page doesn't read like a single
 * metric on repeat. Picked deterministically by seed; downstream copy stays
 * coherent regardless of which subject fires.
 */
const HEADLINE_SUBJECTS: readonly string[] = ['MFA is', 'People are', 'Adoption is']

function pickSubject(seed: string): string {
  return pickVariant(HEADLINE_SUBJECTS, seed, 'headline.subject')
}

/* -------------------------------------------------------------------------- */
/* Headline modifier (italic emphasis word/phrase, score-band-keyed)          */
/* -------------------------------------------------------------------------- */

/**
 * Identity-specific modifier states. Distinct from posture-brief's verbs —
 * the Identity pillar reads as a journey ("there / almost there / mid-stride /
 * lagging") rather than a posture ("strong / fragile"). Score-band-keyed:
 * delta is informational on the rest of the brief but doesn't flip the
 * modifier word inside a band, since adoption journeys read more naturally as
 * stable states than as deltas.
 */
function pickModifier(score: number): string {
  if (score >= 95) return 'there'
  if (score >= 85) return 'almost there'
  if (score >= 55) return 'mid-stride'
  return 'lagging'
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_THERE: readonly string[] = [
  'There. Hold the line.',
  'Top form. Routine watch only.',
  'Adoption is settled.',
  'Everyone is on MFA.',
]

/** Templates take a `{gap}` token (a percentage like "6%"). */
const VERDICT_ALMOST_THERE: readonly string[] = [
  'Almost there. Close the last {gap}.',
  'Almost there. {gap} to close.',
  'Nearly home. {gap} remains.',
]

const VERDICT_ALMOST_THERE_NO_GAP: readonly string[] = [
  'Almost there. One short stretch.',
  'Almost there. Final push.',
  'Nearly home. Last stretch.',
]

/** Templates take a `{count}` token (number word: "Eight"). */
const VERDICT_MID_STRIDE_PEOPLE: readonly string[] = [
  'Mid-stride. {count} need MFA.',
  'In motion. {count} still off.',
  'Halfway there. {count} to go.',
]

const VERDICT_MID_STRIDE: readonly string[] = [
  'People are mid-stride.',
  'Adoption is mid-stride.',
  'Halfway there. Keep pushing.',
]

/** Templates take a `{verb}` token (capitalized imperative). */
const VERDICT_MID_STRIDE_ACTION: readonly string[] = [
  'Mid-stride. {verb} first.',
  'In motion. {verb} next.',
]

/** Templates take a `{count}` token (number word: "Eight"). */
const VERDICT_LAGGING_PEOPLE: readonly string[] = [
  'Lagging. {count} need MFA.',
  'Behind. {count} still without MFA.',
  'Soft adoption. {count} to enrol.',
]

const VERDICT_LAGGING: readonly string[] = [
  'Lagging. Begin with MFA.',
  'Behind. Start with people.',
  'Soft floor. Push adoption.',
]

/** Convert 0-9 into word form, fall back to numerals beyond. */
function numberWord(n: number): string {
  const words = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
  ]
  return words[n] ?? String(n)
}

function pickVerdict(
  score: number,
  criticalCount: number | undefined,
  topAction: IdentityBriefInput['topAction'] | undefined,
  seed: string,
): string {
  if (score >= 95) {
    return pickVariant(VERDICT_THERE, seed, 'verdict.there')
  }

  if (score >= 85) {
    // Compute the gap to 100% for the "Close the last 6%" framing. Round so
    // we render whole-percent gaps; if the gap is < 1% we fall back to the
    // no-gap templates ("Almost there. One short stretch.").
    const gap = Math.max(0, Math.round(100 - score))
    if (gap >= 1 && gap <= 9) {
      const tpl = pickVariant(VERDICT_ALMOST_THERE, seed, 'verdict.almostThere')
      return tpl.replace('{gap}', `${gap}%`)
    }
    return pickVariant(VERDICT_ALMOST_THERE_NO_GAP, seed, 'verdict.almostThereNoGap')
  }

  if (score >= 55) {
    if (typeof criticalCount === 'number' && criticalCount > 0 && criticalCount <= 99) {
      const tpl = pickVariant(VERDICT_MID_STRIDE_PEOPLE, seed, 'verdict.midStridePeople')
      return tpl.replace('{count}', numberWord(criticalCount))
    }
    if (topAction?.title) {
      const verb = cap(imperativeFromAction(topAction.title))
      const tpl = pickVariant(VERDICT_MID_STRIDE_ACTION, seed, 'verdict.midStrideAction')
      return tpl.replace('{verb}', verb)
    }
    return pickVariant(VERDICT_MID_STRIDE, seed, 'verdict.midStride')
  }

  // score < 55 — the lagging band
  if (typeof criticalCount === 'number' && criticalCount > 0 && criticalCount <= 99) {
    const tpl = pickVariant(VERDICT_LAGGING_PEOPLE, seed, 'verdict.laggingPeople')
    return tpl.replace('{count}', numberWord(criticalCount))
  }
  return pickVariant(VERDICT_LAGGING, seed, 'verdict.lagging')
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence (named-people style)                                    */
/* -------------------------------------------------------------------------- */

const WEAKLINK_NONE: readonly string[] = [
  'Identity floor is solid.',
  'No one needs a hand right now.',
  'Adoption holds across the org.',
]

const WEAKLINK_ONE_PERSON: readonly string[] = [
  'One person needs a hand.',
  'A single member is still off MFA.',
  'One holdout to bring on board.',
]

/** Templates take a `{count}` token (number word: "Eight"). */
const WEAKLINK_FEW_PEOPLE: readonly string[] = [
  '{count} people need a hand.',
  '{count} members still need MFA.',
  '{count} to bring on board.',
]

/** Templates take a `{count}` token (number word: "Twelve"). */
const WEAKLINK_MANY_PEOPLE: readonly string[] = [
  '{count} people need MFA today.',
  '{count} members are still off MFA.',
  '{count} need attention this week.',
]

/** Templates take a `{n}` token (raw integer string for big numbers). */
const WEAKLINK_LARGE_COHORT: readonly string[] = [
  '{n} people still need MFA.',
  '{n} members on the list.',
  '{n} need attention this sprint.',
]

function buildWeakLink(
  criticalCount: number | undefined,
  seed: string,
): string {
  if (typeof criticalCount !== 'number' || criticalCount <= 0) {
    return pickVariant(WEAKLINK_NONE, seed, 'weakLink.none')
  }

  if (criticalCount === 1) {
    return pickVariant(WEAKLINK_ONE_PERSON, seed, 'weakLink.onePerson')
  }

  if (criticalCount <= 5) {
    const tpl = pickVariant(WEAKLINK_FEW_PEOPLE, seed, 'weakLink.fewPeople')
    return tpl.replace('{count}', numberWord(criticalCount))
  }

  if (criticalCount <= 9) {
    const tpl = pickVariant(WEAKLINK_MANY_PEOPLE, seed, 'weakLink.manyPeople')
    return tpl.replace('{count}', numberWord(criticalCount))
  }

  // 10+: drop word form, use numerals
  const tpl = pickVariant(WEAKLINK_LARGE_COHORT, seed, 'weakLink.largeCohort')
  return tpl.replace('{n}', String(criticalCount))
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + named risk + recommended action)                             */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'Adoption has climbed sharply over the past 30 days, with new enrolments running ahead of the prior month.',
  'MFA adoption has lifted noticeably in the past 30 days. People are coming on board.',
  'The adoption curve steepened over the past month. Real momentum here.',
]

const TREND_LIFTING: readonly string[] = [
  'Adoption ticked up over the past 30 days, with steady progress on member coverage.',
  'Small gains over the past 30 days, spread across the member base. Things are moving the right way.',
  'Member coverage is up modestly over the past month. Quiet progress.',
]

const TREND_FLAT: readonly string[] = [
  'Adoption has held flat over the past 30 days. Not gaining ground, not losing it either.',
  "The adoption number hasn't moved over the past 30 days. Same story as last month.",
  'Steady-state adoption for the past month, with nothing meaningful shifting either way.',
]

const TREND_SLIPPING: readonly string[] = [
  'Adoption has slipped slightly over the past 30 days. Worth a look when you can.',
  'Coverage is down a touch over the past month. Worth a look when you can.',
  'Modest decline over the past 30 days. Not yet a trend, but keep an eye on it.',
]

const TREND_FALLING: readonly string[] = [
  'Adoption has fallen meaningfully over the past 30 days. Needs eyes on it this week.',
  'Coverage dropped sharply over the past month. Needs eyes on it this week.',
  'Significant decline across the past 30 days. This needs a real response, not next sprint.',
]

/** Templates take a `{phrase}` token (already-bolded action title). */
const RISK_TOP_ACTION: readonly string[] = [
  '{phrase} is the biggest move you can make today on the identity surface.',
  '{phrase} is your biggest opportunity to lift adoption.',
  '{phrase} is still the largest unmet identity risk.',
]

/** Templates take a `{count}` token (already-bolded number-word: "**Eight**"). */
const RISK_PEOPLE: readonly string[] = [
  '{count} enabled members are still off MFA and account for most of the residual risk.',
  '{count} people on the no-MFA list make up most of the remaining gap.',
  '{count} members without MFA are the named cohort to focus on first.',
]

const RISK_HYGIENE: readonly string[] = [
  'No single name dominates the risk picture. The rest is routine identity hygiene.',
  'The remaining items are housekeeping rather than headline gaps.',
  "Nothing dominates the picture. It's housekeeping from here.",
]

const RISK_GENERAL_GAP: readonly string[] = [
  'Several smaller gaps add up; closing the biggest of them lifts adoption fastest.',
  'A handful of smaller items stack up. The biggest of them is the place to start.',
  'Several items add up to hold the floor down. Taking the biggest one lifts adoption fastest.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing and keep the routine reviews on cadence.",
  'Keep it steady and keep the routine reviews running.',
  "You're on the right track. Keep the existing review cadence.",
]

const ACTION_LEAD: readonly string[] = [
  'Take the lead item this week to keep momentum.',
  'Take the lead item this week.',
  'Knock out the lead item this week to keep momentum.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Work the named list first; the rest can wait until next sprint.',
  'Focus on the named cohort first; the rest holds until next sprint.',
  'Take the named items first and let the rest wait.',
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real enrolment week, not routine work.',
  'Plan a focused enrolment push this sprint.',
  'Treat this as an enrolment sprint, not business-as-usual.',
]

function buildDeck(
  score: number,
  deltaValue: number,
  criticalCount: number | undefined,
  topAction: IdentityBriefInput['topAction'] | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (deltaValue >= 3) return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (deltaValue > 0) return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (deltaValue === 0) return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (deltaValue >= -3) return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topAction?.title) {
      const phrase = `**${topAction.title.replace(/[.,;:!?]+$/, '')}**`
      const tpl = pickVariant(RISK_TOP_ACTION, seed, 'deck.risk.topAction')
      return tpl.replace('{phrase}', phrase)
    }
    if (typeof criticalCount === 'number' && criticalCount >= 2) {
      // For people-driven risk, lead with the named cohort. Bold the count
      // so the standfirst Markdown renderer picks it up.
      const word = criticalCount <= 9 ? numberWord(criticalCount) : String(criticalCount)
      const phrase = `**${word}**`
      const tpl = pickVariant(RISK_PEOPLE, seed, 'deck.risk.people')
      return tpl.replace('{count}', phrase)
    }
    if (score >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_GENERAL_GAP, seed, 'deck.risk.generalGap')
  })()

  const action = (() => {
    if (score >= 95) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (score >= 85) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (score >= 55) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose an IdentityBrief from the active identity-pillar state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band "lagging / begin with MFA" verdict. Without this
 * guard the composer would coerce `null → 0`, which lands the loading state
 * in the lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value (a brand-new tenant with
 * zero MFA adoption) and still routes through the normal band logic —
 * only `null`/`undefined` triggers the loading brief.
 */
export function composeIdentityBrief(input: IdentityBriefInput = {}): IdentityBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  // Daily seed: stable per (tenant, local day). Tenants share a seed when
  // unset (still deterministic for tests + anonymous flows).
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.score == null) {
    // Headline subject still rotates by seed so the loading state visually
    // matches the eventual data state (no jarring "MFA is" → "People are"
    // flip when the score arrives). The modifier and copy stay neutral.
    return {
      kicker: formatKicker(now),
      headlineSubject: pickSubject(seed),
      headlineModifier: 'loading',
      weakLink: 'Reading the latest adoption signal.',
      deck: 'Pulling the latest identity data. The brief updates once the scan resolves.',
      verdict: 'Reading adoption…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
    }
  }

  const score = input.score

  const headlineSubject = pickSubject(seed)
  const headlineModifier = pickModifier(score)
  const weakLink = buildWeakLink(input.criticalCount, seed)
  const verdict = pickVerdict(score, input.criticalCount, input.topAction, seed)
  const deck = buildDeck(score, deltaValue, input.criticalCount, input.topAction, seed)

  return {
    kicker: formatKicker(now),
    headlineSubject,
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  HEADLINE_SUBJECTS,
  VERDICT_THERE,
  VERDICT_ALMOST_THERE,
  VERDICT_ALMOST_THERE_NO_GAP,
  VERDICT_MID_STRIDE_PEOPLE,
  VERDICT_MID_STRIDE,
  VERDICT_MID_STRIDE_ACTION,
  VERDICT_LAGGING_PEOPLE,
  VERDICT_LAGGING,
  WEAKLINK_NONE,
  WEAKLINK_ONE_PERSON,
  WEAKLINK_FEW_PEOPLE,
  WEAKLINK_MANY_PEOPLE,
  WEAKLINK_LARGE_COHORT,
}
